import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  getApiErrorMessage,
  getModels,
  updateModelAccountDescription,
} from '../../api'
import { EmptyState, LoadingBlock, MetricCard, Section, SettingExplainer, StatusPill } from '../../components/v2/V2Primitives'
import { asList, compactNumber, modelTitle, readBool } from './v2Utils'

const AUTOMATION_MODE_OPTIONS = [
  ['full_auto', 'Full auto'],
  ['handoff_on_offer', 'Handoff on offer'],
  ['handoff_after_first_purchase', 'Handoff after first purchase'],
]

function modeLabel(value) {
  return AUTOMATION_MODE_OPTIONS.find(([key]) => key === value)?.[1] || value || 'Not set'
}

function modeTone(value) {
  if (value === 'full_auto') return 'warn'
  if (value === 'handoff_on_offer' || value === 'handoff_after_first_purchase') return 'good'
  return 'neutral'
}

export default function AdminV2Automation() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')
  const [models, setModels] = useState([])
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('all')
  const [busyKey, setBusyKey] = useState('')

  async function loadModels() {
    setLoading(true)
    setError('')
    try {
      const result = await getModels()
      setModels(asList(result))
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load automation controls'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let alive = true
    async function load() {
      setLoading(true)
      setError('')
      try {
        const result = await getModels()
        if (alive) setModels(asList(result))
      } catch (err) {
        if (alive) setError(getApiErrorMessage(err, 'Failed to load automation controls'))
      } finally {
        if (alive) setLoading(false)
      }
    }
    load()
    return () => { alive = false }
  }, [])

  const filteredModels = useMemo(() => {
    const q = query.trim().toLowerCase()
    return models.filter((model) => {
      const paused = readBool(model.bot_paused)
      const mode = model.automation_mode || 'full_auto'
      if (q && !modelTitle(model).toLowerCase().includes(q)) return false
      if (filter === 'paused') return paused
      if (filter === 'live') return !paused
      if (filter === 'handoff') return mode !== 'full_auto'
      if (filter === 'full_auto') return mode === 'full_auto'
      return true
    })
  }, [filter, models, query])

  const pausedCount = models.filter((model) => readBool(model.bot_paused)).length
  const liveCount = models.length - pausedCount
  const fullAutoCount = models.filter((model) => (model.automation_mode || 'full_auto') === 'full_auto').length
  const handoffCount = models.length - fullAutoCount

  async function saveModelPatch(model, patch, successMessage) {
    const key = `${model.id}:${Object.keys(patch).join(',')}`
    setBusyKey(key)
    setError('')
    setStatus('')
    try {
      const updated = await updateModelAccountDescription(model.id, patch)
      setModels((current) => current.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)))
      setStatus(successMessage)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to update automation control'))
    } finally {
      setBusyKey('')
    }
  }

  function changeMode(model, automationMode) {
    if (!automationMode || automationMode === model.automation_mode) return
    saveModelPatch(model, { automation_mode: automationMode }, `${modelTitle(model)} automation mode saved.`)
  }

  function togglePause(model) {
    const nextPaused = !readBool(model.bot_paused)
    saveModelPatch(
      model,
      { bot_paused: nextPaused },
      `${modelTitle(model)} bot ${nextPaused ? 'paused' : 'resumed'}.`,
    )
  }

  if (loading) return <LoadingBlock label="Loading automation controls..." />

  return (
    <div className="v2-stack">
      <div className="v2-metrics-grid">
        <MetricCard label="Models" value={compactNumber(models.length)} detail="Visible automation scope" />
        <MetricCard label="Live bots" value={compactNumber(liveCount)} detail="Runtime sends allowed" tone="good" />
        <MetricCard label="Paused bots" value={compactNumber(pausedCount)} detail="Outbound automation stopped" tone={pausedCount ? 'warn' : 'neutral'} />
        <MetricCard label="Handoff modes" value={compactNumber(handoffCount)} detail="Operator review around offers" tone="good" />
      </div>

      {error ? <div className="v2-error">{error}</div> : null}
      {status ? <div className="v2-info">{status}</div> : null}

      <Section
        className="v2-section-automation"
        eyebrow="Automation"
        title="Basic runtime controls"
        description="Change the controls operators need during live work without opening graph or prompt internals."
        actions={(
          <div className="v2-toolbar v2-toolbar-automation">
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search models" />
            <select value={filter} onChange={(event) => setFilter(event.target.value)}>
              <option value="all">All</option>
              <option value="live">Live bots</option>
              <option value="paused">Paused bots</option>
              <option value="handoff">Handoff modes</option>
              <option value="full_auto">Full auto</option>
            </select>
            <button type="button" className="v2-btn v2-btn-ghost" disabled={!!busyKey} onClick={loadModels}>
              Refresh
            </button>
          </div>
        )}
      >
        {filteredModels.length ? (
          <div className="v2-table-card">
            <table className="v2-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Bot status</th>
                  <th>Automation mode</th>
                  <th>Price cap</th>
                  <th>Profile</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredModels.map((model) => {
                  const paused = readBool(model.bot_paused)
                  const mode = model.automation_mode || 'full_auto'
                  const modeBusy = busyKey === `${model.id}:automation_mode`
                  const pauseBusy = busyKey === `${model.id}:bot_paused`
                  return (
                    <tr key={model.id}>
                      <td><Link to={`/v2/models/${model.id}`} className="v2-text-link">{modelTitle(model)}</Link></td>
                      <td><StatusPill tone={paused ? 'bad' : 'good'}>{paused ? 'paused' : 'live'}</StatusPill></td>
                      <td>
                        <label className="v2-field">
                          <span>Mode</span>
                          <select value={mode} disabled={!!busyKey} onChange={(event) => changeMode(model, event.target.value)}>
                            {AUTOMATION_MODE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                          </select>
                        </label>
                      </td>
                      <td>{model.max_message_price == null ? 'Default' : `$${model.max_message_price}`}</td>
                      <td>{model.account_description ? 'Configured' : 'Missing'}</td>
                      <td>
                        <div className="v2-action-row">
                          <StatusPill tone={modeTone(mode)}>{modeLabel(mode)}</StatusPill>
                          <button type="button" className="v2-btn v2-btn-secondary" disabled={!!busyKey} onClick={() => togglePause(model)}>
                            {pauseBusy ? 'Saving...' : paused ? 'Resume bot' : 'Pause bot'}
                          </button>
                          {modeBusy ? <span>Saving mode...</span> : null}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No models match this automation view" description="Try another filter or create a model first." />
        )}
      </Section>

      <Section eyebrow="Basic policy" title="What these controls change" description="The production-safe controls are live; graph and prompt internals stay separated below.">
        <div className="v2-settings-stack">
          <SettingExplainer
            label="Automation mode"
            value="Per model"
            description="Defines whether bot replies, offers and handoffs require manual approval."
            recommended="Handoff on offer for first live rollout."
            impact="Directly affects active conversations and paid offers."
            scope="Model"
            risk="Medium"
          />
          <SettingExplainer
            label="Bot pause"
            value="Immediate"
            description="Stops automatic scheduling and outbound bot sends for the selected model."
            recommended="Use during incidents, account review or provider instability."
            impact="Paused models will not auto-reply until resumed."
            scope="Model"
            risk="Medium"
          />
        </div>
      </Section>

      <Section eyebrow="Advanced" title="Graph and prompt internals" description="Separated from daily workflows so operators can change safe controls without touching graph internals.">
        <div className="v2-advanced-grid">
          <AdvancedCard title="Configurations" />
          <AdvancedCard title="Graph" />
          <AdvancedCard title="Agents and prompts" />
          <AdvancedCard title="Nodes and transitions" />
          <AdvancedCard title="Executor and LLM router" />
          <AdvancedCard title="Proactive rules" />
        </div>
      </Section>
    </div>
  )
}

function AdvancedCard({ title }) {
  return (
    <article className="v2-advanced-card">
      <div>
        <strong>{title}</strong>
        <p>Advanced editing remains isolated from daily runtime controls and still needs a dedicated production editor.</p>
      </div>
      <StatusPill tone="warn">Advanced</StatusPill>
    </article>
  )
}
